import adapter from './adapter';

export const RECENT_DOCUMENTS_PERIOD_ALL = 'all';
export const RECENT_DOCUMENTS_PERIOD_TODAY = 'today';
export const RECENT_DOCUMENTS_PERIOD_YESTERDAY = 'yesterday';
export const RECENT_DOCUMENTS_PERIOD_WEEK = 'week';
export const RECENT_DOCUMENTS_PERIOD_PREV_WEEK = 'prevWeek';

export const FAVORITES_SECTION_ALL = 'all';
export const FAVORITES_SECTION_COMMON = 'common';
export const FAVORITES_SECTION_PRIVATE = 'private';

export default Object.assign(Object.create(adapter), {
  /**
   * @param {number} [limit=5]
   * @returns {Promise<{
   *   recent: Array<ApiDocumentBaseCardDef>,
   *   favorites: Array<ApiDocumentBaseCardDef>,
   *   watchlist: Array<ApiDocumentBaseCardDef>,
   *   subscribes: Array<ApiDocumentBaseCardDef>
   * }>}
   */
  getDocuments(limit = 5) {
    return this.getData('/user.documents', { params: { limit } });
  },

  /**
   * Возвращает последние открытые документы пользователя.
   *
   * @param {string} [period=RECENT_DOCUMENTS_PERIOD_ALL]
   * @param {number} [limit=20]
   * @param {number} [offset=0]
   * @param {boolean} [countTotal=true]
   * @returns {Promise<ApiRecentDocuments>}
   */
  getRecentDocuments(
    period = RECENT_DOCUMENTS_PERIOD_ALL,
    limit = 20,
    offset = 0,
    countTotal = true
  ) {
    return this.getData('/user.documents/recent', {
      params: { period, limit, offset, countTotal: countTotal ? 1 : 0 }
    });
  },

  /**
   * Возвращает избранные документы пользователя.
   *
   * @param {string} [section=FAVORITES_SECTION_ALL]
   * @param {number} [limit=20]
   * @param {number} [offset=0]
   * @returns {Promise<ApiFavoriteDocuments>}
   */
  getFavoriteDocuments(
    section = FAVORITES_SECTION_ALL,
    limit = 20,
    offset = 0
  ) {
    return this.getData('/user.documents/favorites', {
      params: { section, limit, offset }
    });
  },

  /**
   * Возвращает избранные документы пользователя и папки, в которых находятся эти документы.
   *
   * @param {string} [section=FAVORITES_SECTION_ALL]
   * @returns {Promise<Array<ApiFavoriteDocumentsWithFolders>>}
   */
  getFavoriteDocumentsWithFolders(
    section = FAVORITES_SECTION_ALL
  ) {
    return this.getData('user.documents/favorites/folders', {
      params: {
        section : section,
        documents : true
      }
    })
  },

  // Возвращает документы на контроле пользователя.
  getWatchlistDocuments(
    eventId = null,
    periodId = null,
    limit = 20,
    offset = 0
  ) {
    return this.getData('/user.documents/watchlist', {
      params: { eventId, periodId, limit, offset }
    });
  },

  // Возвращает список событий документов на контроле пользователя.
  getWatchlistDocumentEvents() {
    return this.getData('/user.documents/watchlist.events');
  },

  /**
   * Возвращает количество непрочитанных пользователем документов на контроле.
   *
   * @returns {Promise<number>}
   */
  countUnreadWatchlistDocuments() {
    return this.getData('/user.documents/watchlist/unread-count');
  },

  /**
   * Добавляет документ в список избранных документов пользователя или удаляет из него
   * в зависимости от значения параметра isFavorite.
   *
   * @param {string} documentId
   * @param {boolean} isFavorite
   * @returns {Promise<undefined>}
   */
  setDocumentIsFavorite(documentId, isFavorite, favoriteId = '') {
    // TODO: Удалите после реализации API удаления документов из избранного.
    if (!isFavorite && !favoriteId) {
      return Promise.reject(
        new Error(
          'Удаление документов из избранного возможно только на странице «Избранное».'
        )
      );
    }

    let url = isFavorite
      ? '/user.documents/favorites/add'
      : '/user.documents/favorites/delete';

    const id = favoriteId ? { favoriteId } : { documentId };

    return this.post(url, id);
  },

  /**
   * Добавляет документ в список документов на контроле пользователя или удаляет из него
   * в зависимости от значения параметра isWatched.
   *
   * @param {string} documentId
   * @param {boolean} isWatched
   * @returns {Promise<undefined>}
   */
  setDocumentIsWatched(documentId, isWatched) {
    let url = isWatched
      ? '/user.documents/watchlist/add'
      : '/user.documents/watchlist/delete';

    return this.post(url, { documentId });
  },

  /**
   * Отмечает документ на контроле как прочитанный или непрочитанный пользователем
   * в зависимости от значения параметра isRead.
   *
   * @param {string} documentId
   * @param {boolean} isRead
   * @returns {Promise<undefined>}
   */
  setWatchedDocumentAsRead(documentId, isRead) {
    let url = isRead
      ? '/user.documents/watchlist/read'
      : '/user.documents/watchlist/unread';

    return this.post(url, { documentId });
  }
});
